import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  VehicleTypeBrandsResponse,
  VehicleTypeFuelTypesResponse,
  VehicleTypeModelsResponse,
  VehicleTypeTransmissionTypesResponse,
  VehicleTypeVariantsResponse,
  VehicleTypeYearsResponse,
} from '../models/transunion.model';
import { ApiResponse } from '../models/parameter.model';

interface TransunionRequest {
        vehicleTypeCode?: string;
        year?: string;
        brand?: string;
        model?: string;
        fuelType?: string;
        transmissionType?: string;
      }

      @Injectable({
        providedIn: 'root',
      })
      export class TransunionService {
        private http = inject(HttpClient);

        private normalizeResponse<T>(response: ApiResponse<T> | T): ApiResponse<T> {
          if (
            response &&
            typeof response === 'object' &&
            'success' in response &&
            'data' in response
          ) {
            return response as ApiResponse<T>;
          }

          return { success: true, data: response as T, error: null };
        }

        private toErrorResponse<T>(error: any): ApiResponse<T> {
          const message =
            error?.error?.message ||
            error?.message ||
            'Request failed. Please try again later.';
          const code = error?.error?.code || `${error?.status ?? 'HTTP_ERROR'}`;
          return { success: false, data: null, error: { code, message } };
        }

        getVehicleTypes(): Observable<ApiResponse<any>> {

          const request: TransunionRequest = {};
          return this.http
            .post<ApiResponse<any> | any>(
              `/transunion/vehicleTypes`,
              request,
              { headers: this.getHeaders() }
            )
            .pipe(
              map((response) => this.normalizeResponse<any>(response)),
              catchError((error) => of(this.toErrorResponse<any>(error)))
            );
        }

        getVehicleTypeYears(
          vehicleTypeCode: string
        ): Observable<ApiResponse<VehicleTypeYearsResponse>> {
          const request: TransunionRequest = { vehicleTypeCode };
          return this.http
            .post<ApiResponse<VehicleTypeYearsResponse> | VehicleTypeYearsResponse>(
              `/transunion/vehicleTypeYears`,
              request,
              { headers: this.getHeaders() }
            )
            .pipe(
              map((response) =>
                this.normalizeResponse<VehicleTypeYearsResponse>(response)
              ),
              catchError((error) =>
                of(this.toErrorResponse<VehicleTypeYearsResponse>(error))
              )
            );
        }

        getVehicleTypeBrands(
          vehicleTypeCode: string,
          year: string
        ): Observable<ApiResponse<VehicleTypeBrandsResponse>> {
          const request: TransunionRequest = { vehicleTypeCode, year };
          return this.http
            .post<ApiResponse<VehicleTypeBrandsResponse> | VehicleTypeBrandsResponse>(
              `/transunion/vehicleTypeBrands`,
              request,
              { headers: this.getHeaders() }
            )
            .pipe(
              map((response) =>
                this.normalizeResponse<VehicleTypeBrandsResponse>(response)
              ),
              catchError((error) =>
                of(this.toErrorResponse<VehicleTypeBrandsResponse>(error))
              )
            );
        }

        getVehicleTypeModels(
          vehicleTypeCode: string,
          year: string,
          brand: string
        ): Observable<ApiResponse<VehicleTypeModelsResponse>> {
          const request: TransunionRequest = { vehicleTypeCode, year, brand };
          return this.http
            .post<ApiResponse<VehicleTypeModelsResponse> | VehicleTypeModelsResponse>(
              `/transunion/vehicleTypeModels`,
              request,
              { headers: this.getHeaders() }
            )
            .pipe(
              map((response) =>
                this.normalizeResponse<VehicleTypeModelsResponse>(response)
              ),
              catchError((error) =>
                of(this.toErrorResponse<VehicleTypeModelsResponse>(error))
              )
            );
        }

        getVehicleTypeFuelTypes(
          vehicleTypeCode: string,
          year: string,
          brand: string,
          model: string
        ): Observable<ApiResponse<VehicleTypeFuelTypesResponse>> {
          const request: TransunionRequest = { vehicleTypeCode, year, brand, model };
          return this.http
            .post<ApiResponse<VehicleTypeFuelTypesResponse> | VehicleTypeFuelTypesResponse>(
              `/transunion/vehicleTypeFuelTypes`,
              request,
              { headers: this.getHeaders() }
            )
            .pipe(
              map((response) =>
                this.normalizeResponse<VehicleTypeFuelTypesResponse>(response)
              ),
              catchError((error) =>
                of(this.toErrorResponse<VehicleTypeFuelTypesResponse>(error))
              )
            );
        }

        getVehicleTypeTransmissionTypes(
          vehicleTypeCode: string,
          year: string,
          brand: string,
          model: string,
          fuelType: string
        ): Observable<ApiResponse<VehicleTypeTransmissionTypesResponse>> {
          const request: TransunionRequest = { vehicleTypeCode, year, brand, model, fuelType };
          return this.http
            .post<ApiResponse<VehicleTypeTransmissionTypesResponse> | VehicleTypeTransmissionTypesResponse>(
              `/transunion/vehicleTypeTransmissionTypes`,
              request,
              { headers: this.getHeaders() }
            )
            .pipe(
              map((response) =>
                this.normalizeResponse<VehicleTypeTransmissionTypesResponse>(response)
              ),
              catchError((error) =>
                of(this.toErrorResponse<VehicleTypeTransmissionTypesResponse>(error))
              )
            );
        }

        getVehicleTypeVariants(
          vehicleTypeCode: string,
          year: string,
          brand: string,
          model: string,
          fuelType: string,
          transmissionType: string
        ): Observable<ApiResponse<VehicleTypeVariantsResponse>> {
          const request: TransunionRequest = { vehicleTypeCode, year, brand, model, fuelType, transmissionType };
          return this.http
            .post<ApiResponse<VehicleTypeVariantsResponse> | VehicleTypeVariantsResponse>(
              `/transunion/vehicleTypeVariants`,
              request,
              { headers: this.getHeaders() }
            )
            .pipe(
              map((response) =>
                this.normalizeResponse<VehicleTypeVariantsResponse>(response)
              ),
              catchError((error) =>
                of(this.toErrorResponse<VehicleTypeVariantsResponse>(error))
              )
            );
        }
        private getHeaders(): HttpHeaders {
          const token = localStorage.getItem('access_token') || '';
          const host = window.location.hostname.toLowerCase();
          const domainExt = host.match(/\.(com|net)/)?.[0] || '';
          const domainStr = domainExt === '.com' ? 'com' : 'net';
          return new HttpHeaders()
            .set('Authorization', `Bearer ${token}`)
            .set('domain', domainStr);
        }
      }
